// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import dayjs from 'dayjs';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import isToday from 'dayjs/plugin/isToday';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import relativeTime from 'dayjs/plugin/relativeTime';
import type {DeliveryDate} from '../interfaces/delivery.date.type';
import type {BaseOrderLine, OrderLine} from '../interfaces/order.line.type';

dayjs.extend(isToday);
dayjs.extend(relativeTime);

/**
 * Formats a given epoch date number to a readable format;
 *
 * @param date
 */
export const formatDate = (date: number): string => {
	return dayjs(new Date(date)).format('DD/MM/YYYY');
};

/**
 * Get the text 'Today' if date is today instead of the hours.
 *
 * @param date
 * @param todayText
 * @param baseDate
 */
export const getModifiedDate = (date: number, todayText: string, baseDate: Date = new Date()): string => {
	return dayjs(new Date(date)).isToday() ? todayText : dayjs(date).from(dayjs(baseDate), true) + ' ago';
};

export const formatOrderDate = (orderDate: number) => {
	const date = new Date(orderDate);
	const options: Intl.DateTimeFormatOptions = {year: 'numeric', month: '2-digit', day: '2-digit'};
	return date.toLocaleDateString('nl-NL', options);
};

export const formatLastModifiedDate = (date: number) => {
	return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
};

/**
 * Normalizes the delivery date data to a human-readable string.
 *
 * @param deliveryDate
 * @param shortLabel
 * @param shortWeekText
 * @param longWeekText
 */
export const getNormalizedWeekIncludingLabel = (deliveryDate: DeliveryDate, shortLabel: boolean, shortWeekText: string, longWeekText: string): string => {
	if (!deliveryDate) {
		return '';
	}

	const label: string = shortLabel ? shortWeekText : longWeekText;
	return `${label} ${deliveryDate.week} - ${deliveryDate.year}`;
};

/**
 * Returns the year and week numbers for a given year and week string
 *
 * @param deliveryDateString
 */
export const getYearAndWeekFromString = (deliveryDateString: string): DeliveryDate => {
	if ((!deliveryDateString || /^\s*$/.test(deliveryDateString))) {
		throw new Error('Delivery date string is empty or null');
	}

	const yearAndWeek: string[] = deliveryDateString.split('-');
	return {
		year: parseInt(yearAndWeek[0]), week: parseInt(yearAndWeek[1])
	};
};

/**
 * Returns a sorted array of unique delivery dates for the given order lines.
 *
 * @param orderLines
 */
export const getUniqueDeliveryDatesForOrderLines = (orderLines: BaseOrderLine[]): DeliveryDate[] => {
	if (!orderLines) {
		return [];
	}

	const deliveryDates = orderLines.map(line => `${line.year}-${line.week}`);

	// A little workaround for getting the unique delivery dates.
	const uniqueDeliveryDates: string[] = Array.from(new Set(deliveryDates));

	return uniqueDeliveryDates.map((deliveryDateString: string) => {
		return getYearAndWeekFromString(deliveryDateString);
	}).sort((deliveryDate1: DeliveryDate, deliveryDate2: DeliveryDate): number => {
		if (deliveryDate1.year > deliveryDate2.year) {
			return 1;
		}

		if (deliveryDate1.year < deliveryDate2.year) {
			return -1;
		}

		if (deliveryDate1.week > deliveryDate2.week) {
			return 1;
		}

		if (deliveryDate1.week < deliveryDate2.week) {
			return -1;
		}

		return 0;
	});
};

/**
 * Gets a comma separated string of all the weeks for the given order lines.
 *
 * @param orderLines
 * @param shortWeekTest
 * @param longWeekText
 */
export const getWeeksForOrderLines = (orderLines: OrderLine[], shortWeekTest: string, longWeekText: string): string => {
	return getUniqueDeliveryDatesForOrderLines(orderLines)
		.map((deliveryDate: DeliveryDate) => getNormalizedWeekIncludingLabel(deliveryDate, true, shortWeekTest, longWeekText))
		.join(', ');
};
